'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Calculator,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  ArrowRightLeft,
  Copy,
} from 'lucide-react'
import CompensationDonut, { type Segment } from './CompensationDonut'
import {
  calculatePayBreakdown,
  type PayBreakdownInput,
  type PayBreakdownResult,
  type SpecialPay,
} from '@/app/actions'

/* ── Constants ─────────────────────────────────────────── */
const RANKS = [
  'E-1','E-2','E-3','E-4','E-5','E-6','E-7','E-8','E-9',
  'W-1','W-2','W-3','W-4','W-5',
  'O-1','O-2','O-3','O-4','O-5','O-6','O-7','O-8','O-9','O-10',
]

const YOS_OPTIONS = Array.from({ length: 41 }, (_, i) => i) // 0–40

const SPECIAL_PAY_PRESETS: { label: string; amount: number; taxFree: boolean }[] = [
  { label: 'Sea Pay', amount: 250, taxFree: false },
  { label: 'Flight Pay', amount: 250, taxFree: false },
  { label: 'Hazardous Duty', amount: 150, taxFree: false },
  { label: 'Jump Pay', amount: 150, taxFree: false },
  { label: 'COLA', amount: 200, taxFree: true },
  { label: 'Combat Pay (HFP)', amount: 225, taxFree: true },
  { label: 'Combat Zone Tax Excl.', amount: 0, taxFree: true },
  { label: 'BAS Supplement', amount: 0, taxFree: true },
]

const SEGMENT_COLORS = {
  basePay: '#2563eb',
  bah: '#3b82f6',
  bas: '#60a5fa',
  taxAdv: '#10b981',
  special: '#8b5cf6',
  specialTaxFree: '#06b6d4',
}

/* ── Helpers ───────────────────────────────────────────── */
function buildSegments(result: PayBreakdownResult): Segment[] {
  const segs: Segment[] = [
    { label: 'Base Pay', amount: result.basePay, color: SEGMENT_COLORS.basePay },
  ]
  if (result.bah > 0) segs.push({ label: 'BAH', amount: result.bah, color: SEGMENT_COLORS.bah })
  if (result.bas > 0) segs.push({ label: 'BAS', amount: result.bas, color: SEGMENT_COLORS.bas })

  // Group special pays by tax status
  const taxableSpecials = result.specialPays.filter((s) => !s.taxFree && s.amount > 0)
  const taxFreeSpecials = result.specialPays.filter((s) => s.taxFree && s.amount > 0)

  taxableSpecials.forEach((s) => {
    segs.push({ label: s.label, amount: s.amount, color: SEGMENT_COLORS.special })
  })
  taxFreeSpecials.forEach((s) => {
    segs.push({ label: s.label, amount: s.amount, color: SEGMENT_COLORS.specialTaxFree })
  })

  // Tax advantage segment
  if (result.bah + result.bas > 0) {
    const taxFreeIncome = result.bah + result.bas + taxFreeSpecials.reduce((a, s) => a + s.amount, 0)
    const taxAdvantage = Math.round(taxFreeIncome * (0.20 + result.stateTaxRate))
    if (taxAdvantage > 0) {
      segs.push({ label: 'Tax Advantage', amount: taxAdvantage, color: SEGMENT_COLORS.taxAdv })
    }
  }

  return segs
}

/* ── Scenario type ─────────────────────────────────────── */
interface Scenario {
  label: string
  rank: string
  yearsOfService: number
  zip: string
  hasDependents: boolean
  specialPays: SpecialPay[]
  result: PayBreakdownResult | null
  loading: boolean
  error: string | null
}

const defaultScenario = (label: string): Scenario => ({
  label,
  rank: 'E-5',
  yearsOfService: 6,
  zip: '76544',
  hasDependents: false,
  specialPays: [],
  result: null,
  loading: false,
  error: null,
})

/* ── Select input ──────────────────────────────────────── */
function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--text-3)]">
        {label}
      </span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-xl border border-[var(--border)] bg-[var(--surface-2)] px-4 py-3 pr-10 text-sm font-medium text-[var(--text-1)] shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-3)]" />
      </div>
    </label>
  )
}

/* ── Scenario Panel ────────────────────────────────────── */
function ScenarioPanel({
  scenario,
  onUpdate,
  onCalculate,
  isComparing,
  onCopyFrom,
}: {
  scenario: Scenario
  onUpdate: (patch: Partial<Scenario>) => void
  onCalculate: () => void
  isComparing: boolean
  onCopyFrom?: () => void
}) {
  const [showSpecialPays, setShowSpecialPays] = useState(scenario.specialPays.length > 0)

  const addSpecialPay = (preset: typeof SPECIAL_PAY_PRESETS[number]) => {
    onUpdate({
      specialPays: [...scenario.specialPays, { ...preset }],
    })
    setShowSpecialPays(true)
  }

  const removeSpecialPay = (idx: number) => {
    const next = [...scenario.specialPays]
    next.splice(idx, 1)
    onUpdate({ specialPays: next })
  }

  const updateSpecialPay = (idx: number, patch: Partial<SpecialPay>) => {
    const next = [...scenario.specialPays]
    next[idx] = { ...next[idx], ...patch }
    onUpdate({ specialPays: next })
  }

  const total = scenario.result
    ? scenario.result.basePay +
      scenario.result.bah +
      scenario.result.bas +
      scenario.result.specialPays.reduce((a, s) => a + s.amount, 0)
    : 0

  return (
    <div className="flex flex-col gap-5">
      {/* Scenario label */}
      {isComparing && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={scenario.label}
            onChange={(e) => onUpdate({ label: e.target.value })}
            className="flex-1 rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm font-semibold text-[var(--text-1)] focus:border-blue-500 focus:outline-none"
          />
          {onCopyFrom && (
            <button
              onClick={onCopyFrom}
              className="flex items-center gap-1.5 rounded-lg border border-[var(--border)] px-3 py-2 text-xs font-medium text-[var(--text-3)] transition hover:bg-[var(--surface-2)] hover:text-[var(--text-1)]"
              title="Copy from Scenario A"
            >
              <Copy className="h-3.5 w-3.5" /> Copy A
            </button>
          )}
        </div>
      )}

      {/* Core fields */}
      <div className="grid grid-cols-2 gap-3">
        <SelectField
          label="Rank"
          value={scenario.rank}
          onChange={(v) => onUpdate({ rank: v })}
          options={RANKS.map((r) => ({ value: r, label: r }))}
        />
        <SelectField
          label="Years of Service"
          value={String(scenario.yearsOfService)}
          onChange={(v) => onUpdate({ yearsOfService: Number(v) })}
          options={YOS_OPTIONS.map((y) => ({ value: String(y), label: `${y} yr${y !== 1 ? 's' : ''}` }))}
        />
      </div>

      <label className="block">
        <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--text-3)]">
          Duty Station ZIP
        </span>
        <input
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={scenario.zip}
          onChange={(e) => onUpdate({ zip: e.target.value.replace(/\D/g, '').slice(0, 5) })}
          className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-2)] px-4 py-3 text-sm font-medium text-[var(--text-1)] shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          placeholder="e.g. 76544"
        />
      </label>

      {/* Dependents toggle */}
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-3)]">
          Dependents
        </span>
        <div className="flex overflow-hidden rounded-lg border border-[var(--border)]">
          {[false, true].map((val) => (
            <button
              key={String(val)}
              onClick={() => onUpdate({ hasDependents: val })}
              className={`px-4 py-2 text-xs font-semibold transition ${
                scenario.hasDependents === val
                  ? 'bg-blue-600 text-white'
                  : 'bg-[var(--surface-2)] text-[var(--text-3)] hover:text-[var(--text-1)]'
              }`}
            >
              {val ? 'Yes' : 'No'}
            </button>
          ))}
        </div>
      </div>

      {/* Special pays section */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-2)]/50 p-4">
        <button
          onClick={() => setShowSpecialPays(!showSpecialPays)}
          className="flex w-full items-center justify-between text-sm font-semibold text-[var(--text-1)]"
        >
          Special & Incentive Pays
          {showSpecialPays ? (
            <ChevronUp className="h-4 w-4 text-[var(--text-3)]" />
          ) : (
            <ChevronDown className="h-4 w-4 text-[var(--text-3)]" />
          )}
        </button>

        {showSpecialPays && (
          <div className="mt-3 space-y-2">
            {scenario.specialPays.map((sp, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={sp.label}
                  onChange={(e) => updateSpecialPay(idx, { label: e.target.value })}
                  className="flex-1 rounded-lg border border-[var(--border)] bg-[var(--surface-1)] px-3 py-2 text-xs text-[var(--text-1)] focus:outline-none"
                />
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[var(--text-3)]">
                    $
                  </span>
                  <input
                    type="number"
                    value={sp.amount || ''}
                    onChange={(e) => updateSpecialPay(idx, { amount: Number(e.target.value) })}
                    className="w-24 rounded-lg border border-[var(--border)] bg-[var(--surface-1)] py-2 pl-6 pr-2 text-xs text-[var(--text-1)] focus:outline-none"
                    placeholder="0"
                  />
                </div>
                <label className="flex items-center gap-1 text-[10px] font-medium text-[var(--text-3)] whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={sp.taxFree}
                    onChange={(e) => updateSpecialPay(idx, { taxFree: e.target.checked })}
                    className="h-3.5 w-3.5 rounded border-[var(--border)] accent-blue-500"
                  />
                  Tax-free
                </label>
                <button
                  onClick={() => removeSpecialPay(idx)}
                  className="rounded-md p-1 text-[var(--text-3)] transition hover:bg-red-500/10 hover:text-red-500"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}

            {/* Quick-add presets */}
            <div className="flex flex-wrap gap-1.5 pt-2">
              {SPECIAL_PAY_PRESETS.filter(
                (p) => !scenario.specialPays.some((sp) => sp.label === p.label)
              ).map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => addSpecialPay(preset)}
                  className="flex items-center gap-1 rounded-full border border-[var(--border)] px-2.5 py-1 text-[10px] font-medium text-[var(--text-3)] transition hover:border-blue-500/30 hover:bg-blue-500/5 hover:text-blue-500"
                >
                  <Plus className="h-3 w-3" />
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Calculate button */}
      <button
        onClick={onCalculate}
        disabled={scenario.loading || scenario.zip.length < 5}
        className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
      >
        {scenario.loading ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        ) : (
          <Calculator className="h-4 w-4" />
        )}
        Calculate
      </button>

      {scenario.error && (
        <p className="rounded-lg bg-red-500/10 px-4 py-2 text-xs text-red-500">
          {scenario.error}
        </p>
      )}

      {/* Results summary */}
      {scenario.result && (
        <div className="space-y-3 rounded-xl border border-[var(--border)] bg-[var(--surface-2)]/50 p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-medium text-[var(--text-3)]">Monthly Total</span>
            <span className="text-lg font-bold text-[var(--text-1)]">${total.toLocaleString()}</span>
          </div>
          <div className="h-px bg-[var(--border)]" />
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-medium text-[var(--text-3)]">Annual Total</span>
            <span className="text-sm font-semibold text-[var(--text-1)]">${(total * 12).toLocaleString()}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-medium text-[var(--text-3)]">Civilian Equivalent</span>
            <span className="text-sm font-semibold text-emerald-500">
              ${scenario.result.civilianEquivalent.toLocaleString()}
            </span>
          </div>
          {scenario.result.state && (
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-medium text-[var(--text-3)]">State</span>
              <span className="text-xs font-medium text-[var(--text-1)]">
                {scenario.result.state} ({(scenario.result.stateTaxRate * 100).toFixed(1)}% state tax)
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Main component ────────────────────────────────────── */
export default function PayCalculator() {
  const [scenarioA, setScenarioA] = useState<Scenario>(defaultScenario('Current'))
  const [scenarioB, setScenarioB] = useState<Scenario>(defaultScenario('What If'))
  const [isComparing, setIsComparing] = useState(false)

  const runCalculation = useCallback(async (
    scenario: Scenario,
    setSc: (fn: (prev: Scenario) => Scenario) => void
  ) => {
    setSc((prev) => ({ ...prev, loading: true, error: null }))
    try {
      const input: PayBreakdownInput = {
        rank: scenario.rank,
        yearsOfService: scenario.yearsOfService,
        zip: scenario.zip,
        hasDependents: scenario.hasDependents,
        specialPays: scenario.specialPays.filter((s) => s.amount > 0),
      }
      const result = await calculatePayBreakdown(input)
      setSc((prev) => ({ ...prev, result, loading: false }))
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Calculation failed'
      setSc((prev) => ({ ...prev, error: msg, loading: false }))
    }
  }, [])

  // Auto-calculate on first load with defaults
  useEffect(() => {
    runCalculation(scenarioA, setScenarioA)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const segmentsA = useMemo(
    () => (scenarioA.result ? buildSegments(scenarioA.result) : []),
    [scenarioA.result]
  )
  const segmentsB = useMemo(
    () => (scenarioB.result ? buildSegments(scenarioB.result) : []),
    [scenarioB.result]
  )

  // Shared max for both donuts when comparing
  const sharedMax = useMemo(() => {
    const totalA = segmentsA.reduce((s, seg) => s + seg.amount, 0)
    const totalB = segmentsB.reduce((s, seg) => s + seg.amount, 0)
    return Math.max(totalA, totalB)
  }, [segmentsA, segmentsB])

  const totalA = segmentsA.reduce((s, seg) => s + seg.amount, 0)
  const totalB = segmentsB.reduce((s, seg) => s + seg.amount, 0)
  const delta = totalB - totalA

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-1)]">Pay Calculator</h1>
          <p className="mt-1 text-sm text-[var(--text-3)]">
            Model your total military compensation and see what life changes mean for your paycheck.
          </p>
        </div>
        <button
          onClick={() => {
            if (!isComparing) {
              // Copy scenario A into B as a starting point
              setScenarioB({
                ...scenarioA,
                label: 'What If',
                result: null,
              })
            }
            setIsComparing(!isComparing)
          }}
          className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${
            isComparing
              ? 'border-blue-500 bg-blue-500/10 text-blue-500'
              : 'border-[var(--border)] text-[var(--text-2)] hover:border-blue-500/30 hover:text-blue-500'
          }`}
        >
          <ArrowRightLeft className="h-4 w-4" />
          {isComparing ? 'Exit Compare' : 'Compare Scenarios'}
        </button>
      </div>

      {/* Main layout */}
      <div className={`grid gap-8 ${isComparing ? 'lg:grid-cols-2' : 'lg:grid-cols-[1fr_1.2fr]'}`}>
        {/* Scenario A */}
        <div className="space-y-6">
          {isComparing && (
            <h2 className="text-sm font-bold uppercase tracking-wider text-blue-500">Scenario A</h2>
          )}

          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
            <ScenarioPanel
              scenario={scenarioA}
              onUpdate={(patch) => setScenarioA((prev) => ({ ...prev, ...patch }))}
              onCalculate={() => runCalculation(scenarioA, setScenarioA)}
              isComparing={isComparing}
            />
          </div>

          {/* Donut A */}
          {scenarioA.result && (
            <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
              <CompensationDonut
                segments={segmentsA}
                maxTotal={isComparing ? sharedMax : undefined}
                variant="light"
              />
            </div>
          )}
        </div>

        {/* Scenario B (compare mode) or Donut A large (single mode) */}
        {isComparing ? (
          <div className="space-y-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-violet-500">Scenario B</h2>

            <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
              <ScenarioPanel
                scenario={scenarioB}
                onUpdate={(patch) => setScenarioB((prev) => ({ ...prev, ...patch }))}
                onCalculate={() => runCalculation(scenarioB, setScenarioB)}
                isComparing={isComparing}
                onCopyFrom={() =>
                  setScenarioB((prev) => ({
                    ...prev,
                    rank: scenarioA.rank,
                    yearsOfService: scenarioA.yearsOfService,
                    zip: scenarioA.zip,
                    hasDependents: scenarioA.hasDependents,
                    specialPays: [...scenarioA.specialPays],
                    result: null,
                  }))
                }
              />
            </div>

            {scenarioB.result && (
              <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
                <CompensationDonut
                  segments={segmentsB}
                  maxTotal={sharedMax}
                  variant="light"
                />
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Comparison delta banner */}
      {isComparing && scenarioA.result && scenarioB.result && (
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-around">
            <div className="text-center">
              <p className="text-xs font-medium text-[var(--text-3)]">{scenarioA.label}</p>
              <p className="text-xl font-bold text-[var(--text-1)]">${totalA.toLocaleString()}/mo</p>
            </div>
            <div className="text-center">
              <ArrowRightLeft className="mx-auto mb-1 h-5 w-5 text-[var(--text-3)]" />
              <p
                className={`text-xl font-bold ${
                  delta > 0 ? 'text-emerald-500' : delta < 0 ? 'text-red-500' : 'text-[var(--text-3)]'
                }`}
              >
                {delta > 0 ? '+' : ''}${delta.toLocaleString()}/mo
              </p>
              <p className="text-xs text-[var(--text-3)]">
                {delta > 0 ? '+' : ''}${(delta * 12).toLocaleString()}/yr
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs font-medium text-[var(--text-3)]">{scenarioB.label}</p>
              <p className="text-xl font-bold text-[var(--text-1)]">${totalB.toLocaleString()}/mo</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
